// fallow-ignore-file
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSupplier } from '../queryKeys';

import { addNetworkSupplier } from './addNetworkSupplier';
import { type AddNetworkSupplierMutationOptions } from './types';

/**
 * Mutation key for adding an on-network supplier.
 * Used with `useMutationState` to track pending additions per supplier.
 */
export const MUTATION_KEY_ADD_NETWORK_SUPPLIER = [
  'supplier',
  'networkAdd',
] as const;

/**
 * React Query mutation hook for adding an on-network supplier as a DataExchange
 * partner.
 *
 * Concurrent calls are allowed: each invocation targets a distinct supplier
 * (different `supplierCompanyId`), so the requests do not conflict in payload
 * and can run in parallel.
 *
 * To avoid an invalidation storm during rapid bursts of clicks, `onSettled`
 * only invalidates the supplier overview and network queries when this
 * mutation is the last in-flight `['supplier', ...]` mutation. This coalesces
 * a burst (across both relevance toggles and network adds) into a single
 * refetch on completion. `onSettled` is used rather than `onSuccess` so that
 * earlier successes still get reconciled even when the burst ends with a
 * failed mutation.
 *
 * Consumers can override `mutationKey` or any callback via options.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/post_suppliers_network_add_suppliers_network_add_post
 */
export const useMutationAddNetworkSupplier = (
  options?: AddNetworkSupplierMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => addNetworkSupplier(payload),
    mutationKey: MUTATION_KEY_ADD_NETWORK_SUPPLIER,
    onSettled(_data, _error, variables) {
      // Only invalidate when this is the last in-flight supplier mutation so a
      // burst of clicks coalesces into a single refetch. The current mutation
      // is still counted as `pending` here, hence `=== 1`.
      if (queryClient.isMutating({ mutationKey: ['supplier'] }) === 1) {
        void queryClient.invalidateQueries({
          queryKey: queryKeysSupplier.getNetwork(),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeysSupplier.getOverview(),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeysSupplier.getPriority(variables.recipientCompanyId),
        });
      }
    },
    throwOnError: false,
    ...options,
  });
};
